import React, { createContext, useState, useContext, useEffect } from 'react';
import { api, decodeJwtRole } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'dealer' | 'partner' | 'photographer'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedType = localStorage.getItem('userType');
    if (token && savedType === 'photographer') {
      // Photographers have no /me profile endpoint — reconstruct identity from the JWT.
      const claims = decodeJwtRole(token);
      if (claims.role === 'photographer' || claims.role === 'admin') {
        setUser({ email: claims.email, user_id: claims.user_id, role: claims.role });
        setUserType('photographer');
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('userType');
      }
      setLoading(false);
    } else if (token && savedType) {
      const profileFn = savedType === 'partner' ? api.getPartnerProfile : api.getProfile;
      profileFn()
        .then(profile => {
          setUser(profile);
          setUserType(savedType);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('userType');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const loginPhotographer = async (email, password) => {
    const data = await api.photographerLogin(email, password);
    const claims = decodeJwtRole(data.access_token);
    if (claims.role !== 'photographer' && claims.role !== 'admin') {
      throw new Error('This account is not a photographer account.');
    }
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('userType', 'photographer');
    const profile = { email: email, user_id: claims.user_id, role: claims.role };
    setUser(profile);
    setUserType('photographer');
    return profile;
  };

  const login = async (email, password) => {
    const data = await api.login(email, password);
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('userType', 'dealer');
    const profile = await api.getProfile();
    setUser(profile);
    setUserType('dealer');
    return profile;
  };

  const loginPartner = async (email, password) => {
    const data = await api.partnerLogin(email, password);
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('userType', 'partner');
    const profile = await api.getPartnerProfile();
    setUser(profile);
    setUserType('partner');
    return profile;
  };

  const logout = async () => {
    try {
      await api.request('/auth/logout', { method: 'POST' });
    } catch (e) {
      // Proceed with local logout even if API call fails
    }
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    setUser(null);
    setUserType(null);
  };

  const refreshProfile = async () => {
    const profileFn = userType === 'partner' ? api.getPartnerProfile : api.getProfile;
    const profile = await profileFn();
    setUser(profile);
  };

  return (
    <AuthContext.Provider value={{ user, userType, login, loginPartner, loginPhotographer, logout, loading, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
